import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  adminApi,
  type CreateRecipientLocationBody,
  type PostalRouteRow,
  type RecipientLocationRow,
} from "../lib/admin-api";
import { getExpiryStatus } from "../lib/recipient-expiry";
import {
  EMPTY_MANUAL_ADDRESS,
  formatCarrierLabelAddress,
  postalRouteToManualAddress,
  validateManualAddress,
  type ManualAddressFields,
} from "./manual-address";

const POSTAL_MODES = [
  "UK_DOMESTIC",
  "TRAINING_ESTABLISHMENT",
  "RFA_SHIP",
  "BFPO",
  "HOME",
  "OVERSEAS",
  "ADDRESS_CHANGING",
  "TEMPORARILY_UNAVAILABLE",
];

type EditorState =
  | { kind: "new-destination" }
  | { kind: "edit-route"; route: PostalRouteRow; location: RecipientLocationRow }
  | { kind: "add-route"; location: RecipientLocationRow }
  | null;

export default function RecipientEditor() {
  const [locations, setLocations] = useState<RecipientLocationRow[]>([]);
  const [routes, setRoutes] = useState<PostalRouteRow[]>([]);
  const [editor, setEditor] = useState<EditorState>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validUntilDraft, setValidUntilDraft] = useState("");

  const active = locations.find((location) => location.status === "ACTIVE");
  const activeRoute = routes.find((route) => route.status === "ACTIVE" && route.recipient_location_id === active?.id);

  const refresh = useCallback(async () => {
    const [locationResponse, routeResponse] = await Promise.all([
      adminApi.listRecipientLocations(),
      adminApi.listPostalRoutes(),
    ]);
    setLocations(locationResponse.locations);
    setRoutes(routeResponse.routes);
  }, []);

  useEffect(() => {
    refresh().catch((err: unknown) => setError(errorMessage(err)));
  }, [refresh]);

  useEffect(() => {
    setValidUntilDraft(active?.valid_until?.slice(0, 10) ?? "");
  }, [active?.id, active?.valid_until]);

  async function runAction(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function activateDestination(location: RecipientLocationRow, route: PostalRouteRow | undefined) {
    if (!route) {
      setEditor({ kind: "add-route", location });
      setError("Add and confirm the complete postal address before activating this destination.");
      return;
    }
    await runAction(async () => {
      await adminApi.patchPostalRoute(route.id, {
        activate: true,
        routingReferenceVerified: true,
        markVerified: true,
      });
      await adminApi.patchRecipientLocation(location.id, {
        activate: true,
        receivingMail: true,
        markVerified: true,
      });
      setEditor(null);
    });
  }

  return (
    <div className="stack">
      <div>
        <h2 style={{ marginBottom: "var(--space-2)" }}>Saved addresses</h2>
        <p className="field-help" style={{ margin: 0 }}>
          Edit label details, give each address an app display name, or schedule when the next address takes over.
        </p>
      </div>

      {error && (
        <div className="banner banner-danger" role="alert">
          <span>{error}</span>
        </div>
      )}

      <section className="card stack-sm" aria-labelledby="current-destination-heading">
        <div className="admin-section-heading">
          <strong id="current-destination-heading">Current address</strong>
          {active && <span className="status-pill status-active">Active</span>}
        </div>

        {active ? (
          <>
            <div>
              <strong>{active.establishment_name ?? "Unnamed destination"}</strong>
              <div className="field-help">{formatMode(active.postal_mode)}</div>
            </div>

            {activeRoute ? (
              <AddressSummary route={activeRoute} />
            ) : (
              <div className="banner banner-warn">
                <span>No active postal address is configured for this destination.</span>
              </div>
            )}

            <div className="admin-action-row">
              {activeRoute ? (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setEditor({ kind: "edit-route", route: activeRoute, location: active })}
                  disabled={busy}
                >
                  Edit saved address
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => setEditor({ kind: "add-route", location: active })}
                  disabled={busy}
                >
                  Add full address
                </button>
              )}
              {activeRoute && (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() =>
                    runAction(async () => {
                      await adminApi.patchPostalRoute(activeRoute.id, {
                        routingReferenceVerified: true,
                        markVerified: true,
                      });
                      await adminApi.patchRecipientLocation(active.id, { markVerified: true });
                    })
                  }
                  disabled={busy}
                >
                  Mark destination verified today
                </button>
              )}
            </div>

            <div className="admin-status-grid">
              <div>
                <span className="field-help">Receiving post</span>
                <strong>{active.receiving_mail ? "Yes" : "No — paused"}</strong>
              </div>
              <div>
                <span className="field-help">Last checked</span>
                <strong>{active.last_verified_at ? new Date(active.last_verified_at).toLocaleDateString("en-GB") : "Not yet"}</strong>
              </div>
              <div>
                <span className="field-help">Forces Free Mail</span>
                <strong>{active.free_mail_eligible ? "Explicitly eligible" : "Not marked eligible"}</strong>
              </div>
            </div>

            {(() => {
              const expiryStatus = getExpiryStatus(active);
              if (!expiryStatus) return null;
              return (
                <div className={`banner ${expiryStatus.kind === "expired" ? "banner-danger" : "banner-warn"}`} role="alert">
                  <span>{expiryStatus.message}</span>
                </div>
              );
            })()}

            <div className="field">
              <label htmlFor="validUntil">Receiving until</label>
              <span className="field-help">Leave blank for no automatic expiry.</span>
              <div className="admin-inline-control">
                <input
                  id="validUntil"
                  type="date"
                  value={validUntilDraft}
                  onChange={(event) => setValidUntilDraft(event.target.value)}
                />
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() =>
                    runAction(() => adminApi.patchRecipientLocation(active.id, { validUntil: validUntilDraft || null }).then(() => undefined))
                  }
                  disabled={busy || validUntilDraft === (active.valid_until?.slice(0, 10) ?? "")}
                >
                  Save date
                </button>
              </div>
            </div>

            <button
              className="btn btn-secondary"
              style={{ width: "fit-content" }}
              type="button"
              onClick={() =>
                runAction(() => adminApi.patchRecipientLocation(active.id, { receivingMail: !active.receiving_mail }).then(() => undefined))
              }
              disabled={busy}
            >
              {active.receiving_mail ? "Pause post" : "Resume receiving post"}
            </button>
            <button
              className="btn btn-secondary"
              style={{ width: "fit-content" }}
              type="button"
              onClick={() =>
                runAction(() => adminApi.patchRecipientLocation(active.id, { freeMailEligible: !active.free_mail_eligible }).then(() => undefined))
              }
              disabled={busy}
            >
              {active.free_mail_eligible ? "Remove Free Mail eligibility" : "Mark explicitly eligible for Free Mail"}
            </button>
          </>
        ) : (
          <div className="banner banner-danger">
            <span>No active destination — the public wizard will refuse to start shipments.</span>
          </div>
        )}
      </section>

      {editor?.kind === "edit-route" && (
        <ManualAddressEditor
          title={`Edit ${editor.location.establishment_name ?? "saved address"}`}
          initial={postalRouteToManualAddress(editor.route)}
          initialDisplayName={editor.location.establishment_name ?? ""}
          initialStartDate={editor.location.status === "ACTIVE" ? "" : editor.location.valid_from?.slice(0, 10) ?? ""}
          busy={busy}
          activeRoute={editor.location.status === "ACTIVE" && editor.route.status === "ACTIVE"}
          onCancel={() => setEditor(null)}
          onSave={(address, activate, metadata) =>
            runAction(async () => {
              await adminApi.patchPostalRoute(editor.route.id, {
                ...address,
                activate,
                markVerified: address.routingReferenceVerified,
              });
              await adminApi.patchRecipientLocation(editor.location.id, {
                establishmentName: metadata.displayName,
                ...(activate ? { activate: true, receivingMail: true } : { validFrom: metadata.startDate || null }),
                markVerified: address.routingReferenceVerified,
              });
              setEditor(null);
            })
          }
        />
      )}

      {editor?.kind === "add-route" && (
        <ManualAddressEditor
          title={`Add address for ${editor.location.establishment_name ?? "destination"}`}
          initial={EMPTY_MANUAL_ADDRESS}
          initialDisplayName={editor.location.establishment_name ?? ""}
          initialStartDate=""
          busy={busy}
          activeRoute={false}
          onCancel={() => setEditor(null)}
          onSave={(address, activate, metadata) =>
            runAction(async () => {
              const response = await adminApi.createPostalRoute({ recipientLocationId: editor.location.id, ...address });
              await adminApi.patchRecipientLocation(editor.location.id, {
                establishmentName: metadata.displayName,
                ...(!activate ? { validFrom: metadata.startDate || null } : {}),
              });
              if (activate) {
                await adminApi.patchPostalRoute(response.route.id, {
                  activate: true,
                  routingReferenceVerified: true,
                  markVerified: true,
                });
                if (editor.location.status !== "ACTIVE") {
                  await adminApi.patchRecipientLocation(editor.location.id, {
                    activate: true,
                    receivingMail: true,
                    markVerified: true,
                  });
                }
              }
              setEditor(null);
            })
          }
        />
      )}

      {editor?.kind === "new-destination" && (
        <NewDestinationForm
          busy={busy}
          onCancel={() => setEditor(null)}
          onSave={(body) =>
            runAction(async () => {
              await adminApi.createRecipientLocation(body);
              setEditor(null);
            })
          }
        />
      )}

      <section className="card stack-sm" aria-labelledby="destination-history-heading">
        <div className="admin-section-heading">
          <strong id="destination-history-heading">All saved addresses</strong>
          <button className="btn btn-primary" type="button" onClick={() => setEditor({ kind: "new-destination" })} disabled={busy}>
            Add new address
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Display name</th>
                <th>Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => {
                const locationRoutes = routes.filter((route) => route.recipient_location_id === location.id);
                const route = locationRoutes.find((candidate) => candidate.status === "ACTIVE") ?? locationRoutes[0];
                return (
                  <tr key={location.id}>
                    <td>
                      <strong>{location.establishment_name ?? "Unnamed"}</strong>
                      <div className="field-help">{formatMode(location.postal_mode)}</div>
                      {location.free_mail_eligible === 1 && <div className="field-help">Forces Free Mail eligible</div>}
                    </td>
                    <td>{route ? route.postcode ?? "No postcode" : "No address"}</td>
                    <td>{formatSavedAddressStatus(location)}</td>
                    <td>
                      <div className="admin-table-actions">
                        {route ? (
                          <button
                            className="btn btn-ghost"
                            type="button"
                            onClick={() => setEditor({ kind: "edit-route", route, location })}
                            disabled={busy}
                          >
                            Edit address
                          </button>
                        ) : (
                          <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => setEditor({ kind: "add-route", location })}
                            disabled={busy}
                          >
                            Add address
                          </button>
                        )}
                        {location.status !== "ACTIVE" && (
                          <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => activateDestination(location, route)}
                            disabled={busy || !route}
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AddressSummary({ route }: { route: PostalRouteRow }) {
  const lines = formatCarrierLabelAddress(postalRouteToManualAddress(route));
  return (
    <div className="admin-address-summary">
      <div>
        <span className="field-help">Address as it will appear on the label</span>
        <address>
          {lines.map((line, index) => (
            <div key={`${line}-${index}`}>{line}</div>
          ))}
        </address>
      </div>
      <div className="admin-address-flags">
        <span className={`status-pill ${route.routing_reference_verified ? "status-active" : "status-warning"}`}>
          {route.routing_reference_verified ? "Address confirmed" : "Needs confirmation"}
        </span>
        <span className="field-help">
          Last checked: {route.last_verified_at ? new Date(route.last_verified_at).toLocaleDateString("en-GB") : "not yet"}
        </span>
      </div>
    </div>
  );
}

function NewDestinationForm({
  busy,
  onSave,
  onCancel,
}: {
  busy: boolean;
  onSave: (body: CreateRecipientLocationBody) => Promise<void>;
  onCancel: () => void;
}) {
  const [postalMode, setPostalMode] = useState("TRAINING_ESTABLISHMENT");
  const [displayName, setDisplayName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [receivingMail, setReceivingMail] = useState(true);
  const [freeMailEligible, setFreeMailEligible] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState<ManualAddressFields>({ ...EMPTY_MANUAL_ADDRESS });
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit(activate: boolean) {
    const nextErrors = validateManualAddress(address);
    if (!displayName.trim()) nextErrors.displayName = "Enter a display name for this saved address.";
    if ((activate || startDate) && !address.routingReferenceVerified) {
      nextErrors.routingReferenceVerified = "Confirm that you have checked the full address before activating or scheduling it.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    await onSave({
      postalMode,
      establishmentName: displayName.trim(),
      receivingMail,
      freeMailEligible,
      validFrom: activate ? null : startDate || null,
      validUntil: validUntil || null,
      notes: notes.trim(),
      activate,
      address,
    });
  }

  return (
    <section className="card stack" aria-labelledby="new-destination-heading">
      <div>
        <h3 id="new-destination-heading" style={{ marginBottom: "var(--space-1)" }}>New destination and address</h3>
        <p className="field-help" style={{ margin: 0 }}>Save it for later, schedule it to take over automatically, or use it now.</p>
      </div>

      <div className="admin-address-grid">
        <Field label="Postal mode" id="postalMode">
          <select id="postalMode" value={postalMode} onChange={(event) => setPostalMode(event.target.value)}>
            {POSTAL_MODES.map((mode) => <option key={mode} value={mode}>{formatMode(mode)}</option>)}
          </select>
        </Field>
        <Field label="Display name" id="displayName" required error={errors.displayName} help="For example: HMS Collingwood. This does not go on the label.">
          <input id="displayName" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        </Field>
        <Field label="Start date (optional)" id="newStartDate" help="On this date, this address automatically replaces the current one.">
          <input id="newStartDate" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </Field>
        <Field label="Receiving until (optional)" id="newValidUntil">
          <input id="newValidUntil" type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} />
        </Field>
        <Field label="Destination notes (optional)" id="destinationNotes">
          <input id="destinationNotes" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>

      <label className="admin-check-row">
        <input type="checkbox" checked={receivingMail} onChange={(event) => setReceivingMail(event.target.checked)} />
        This destination is receiving mail
      </label>
      <label className="admin-check-row">
        <input type="checkbox" checked={freeMailEligible} onChange={(event) => setFreeMailEligible(event.target.checked)} />
        Explicitly eligible for the Enduring Families Free Mail service
      </label>
      <p className="field-help">Do not infer this from a ship name. Enable it only after checking the current GOV.UK eligibility list.</p>

      <ManualAddressInputs fields={address} onChange={setAddress} errors={errors} />
      <AddressPreview fields={address} />

      <div className="admin-action-row">
        <button className="btn btn-secondary" type="button" onClick={() => submit(false)} disabled={busy}>
          {startDate ? "Schedule address" : "Save address"}
        </button>
        <button className="btn btn-primary" type="button" onClick={() => submit(true)} disabled={busy}>Use now</button>
        <button className="btn btn-ghost" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </section>
  );
}

function ManualAddressEditor({
  title,
  initial,
  initialDisplayName,
  initialStartDate,
  busy,
  activeRoute,
  onSave,
  onCancel,
}: {
  title: string;
  initial: ManualAddressFields;
  initialDisplayName: string;
  initialStartDate: string;
  busy: boolean;
  activeRoute: boolean;
  onSave: (
    fields: ManualAddressFields,
    activate: boolean,
    metadata: { displayName: string; startDate: string },
  ) => Promise<void>;
  onCancel: () => void;
}) {
  const [fields, setFields] = useState<ManualAddressFields>({ ...initial });
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit(activate: boolean) {
    const nextErrors = validateManualAddress(fields);
    if (!displayName.trim()) nextErrors.displayName = "Enter a display name for this saved address.";
    if ((activeRoute || activate || startDate) && !fields.routingReferenceVerified) {
      nextErrors.routingReferenceVerified = "Confirm that you have checked the full address before it is used.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      await onSave(fields, activate, { displayName: displayName.trim(), startDate });
    }
  }

  return (
    <section className="card stack" aria-labelledby="manual-address-heading">
      <div>
        <h3 id="manual-address-heading" style={{ marginBottom: "var(--space-1)" }}>{title}</h3>
        <p className="field-help" style={{ margin: 0 }}>
          Edit the saved details below. The display name is for the app only and is never printed on the carrier label.
        </p>
      </div>
      <div className="admin-address-grid">
        <Field label="Display name" id="savedAddressDisplayName" required error={errors.displayName} wide help="For example: HMS Collingwood. This does not go on the label.">
          <input id="savedAddressDisplayName" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        </Field>
        {!activeRoute && (
          <Field label="Start date (optional)" id="savedAddressStartDate" help="On this date, this address automatically replaces the current one.">
            <input id="savedAddressStartDate" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </Field>
        )}
      </div>
      <ManualAddressInputs fields={fields} onChange={setFields} errors={errors} />
      <AddressPreview fields={fields} />
      <div className="admin-action-row">
        {activeRoute ? (
          <button className="btn btn-primary" type="button" onClick={() => submit(false)} disabled={busy}>Save changes</button>
        ) : (
          <>
            <button className="btn btn-secondary" type="button" onClick={() => submit(false)} disabled={busy}>
              {startDate ? "Save scheduled address" : "Save changes"}
            </button>
            <button className="btn btn-primary" type="button" onClick={() => submit(true)} disabled={busy}>Use now</button>
          </>
        )}
        <button className="btn btn-ghost" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </section>
  );
}

function ManualAddressInputs({
  fields,
  onChange,
  errors,
}: {
  fields: ManualAddressFields;
  onChange: (fields: ManualAddressFields) => void;
  errors: Record<string, string>;
}) {
  const set = (key: keyof ManualAddressFields, value: string | boolean) => onChange({ ...fields, [key]: value });
  return (
    <fieldset className="admin-address-fieldset">
      <legend>Postal label</legend>
      <p className="field-help">Type the finished label in order. Combine details onto one line yourself where needed.</p>
      <div className="admin-address-grid">
        <Field label="Recipient / addressee" id="addressLine1" required error={errors.addressLine1} wide>
          <input id="addressLine1" value={fields.addressLine1} onChange={(event) => set("addressLine1", event.target.value)} />
        </Field>
        <Field label="Organisation / department (optional)" id="addressLine2" wide help="Include any role, service number or class details here, combined onto this one line.">
          <input id="addressLine2" value={fields.addressLine2} onChange={(event) => set("addressLine2", event.target.value)} />
        </Field>
        <Field label="Address line 1" id="addressLine4" required error={errors.addressLine4} wide>
          <input id="addressLine4" value={fields.addressLine4} onChange={(event) => set("addressLine4", event.target.value)} />
        </Field>
        <Field label="Address line 2 (optional)" id="addressLine5" wide>
          <input id="addressLine5" value={fields.addressLine5} onChange={(event) => set("addressLine5", event.target.value)} />
        </Field>
        <Field label="Address line 3 (optional)" id="addressLine6" wide>
          <input id="addressLine6" value={fields.addressLine6} onChange={(event) => set("addressLine6", event.target.value)} />
        </Field>
        <Field label="Town / city" id="townCity" required error={errors.townCity}>
          <input id="townCity" value={fields.townCity} onChange={(event) => set("townCity", event.target.value)} />
        </Field>
        <Field label="County / region" id="county">
          <input id="county" value={fields.county} onChange={(event) => set("county", event.target.value)} />
        </Field>
        <Field label="Postcode" id="postcode" required error={errors.postcode}>
          <input id="postcode" autoCapitalize="characters" value={fields.postcode} onChange={(event) => set("postcode", event.target.value.toUpperCase())} />
        </Field>
        <Field label="Country" id="country" required error={errors.country}>
          <input id="country" value={fields.country} onChange={(event) => set("country", event.target.value)} />
        </Field>
        <Field label="BFPO number (only when applicable)" id="bfpoNumber">
          <input id="bfpoNumber" value={fields.bfpoNumber} onChange={(event) => set("bfpoNumber", event.target.value)} />
        </Field>
      </div>
      <label className="admin-check-row">
        <input
          type="checkbox"
          checked={fields.routingReferenceVerified}
          onChange={(event) => set("routingReferenceVerified", event.target.checked)}
        />
        I have checked the finished label below
      </label>
      {errors.routingReferenceVerified && <span className="field-error">{errors.routingReferenceVerified}</span>}
    </fieldset>
  );
}

function AddressPreview({ fields }: { fields: ManualAddressFields }) {
  const lines = useMemo(() => formatCarrierLabelAddress(fields), [fields]);
  return (
    <div className="admin-address-preview">
      <strong>Carrier label preview</strong>
      <span className="field-help">This is the line order saved for carrier labels.</span>
      {lines.length > 0 ? (
        <address>{lines.map((line, index) => <div key={`${line}-${index}`}>{line}</div>)}</address>
      ) : (
        <span className="field-help">Start typing to preview the finished address.</span>
      )}
    </div>
  );
}

function Field({
  label,
  id,
  required,
  error,
  help,
  wide,
  children,
}: {
  label: string;
  id: string;
  required?: boolean;
  error?: string;
  help?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`field${wide ? " admin-field-wide" : ""}`}>
      <label htmlFor={id}>{label}{required ? " *" : ""}</label>
      {help && <span className="field-help">{help}</span>}
      {children}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

function formatMode(mode: string): string {
  return mode.replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function formatSavedAddressStatus(location: RecipientLocationRow): string {
  if (location.status === "ACTIVE") return "Active";
  if (location.status === "DRAFT" && location.valid_from) {
    const startDate = new Date(`${location.valid_from.slice(0, 10)}T12:00:00Z`);
    return `Scheduled for ${startDate.toLocaleDateString("en-GB", { timeZone: "Europe/London" })}`;
  }
  return location.status === "ARCHIVED" ? "Previous" : "Saved draft";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The address could not be saved. Please try again.";
}
